/**
 * result-handler.ts - Match Result Submission and Handling
 *
 * Handles submitting match results to the API and
 * updating the UI with visual feedback.
 */

import { API_BASE_URL } from '../../utils/constants';
import { showWarningConfirmDialog } from '../../utils/confirm-dialog';
import { t } from '../../utils/i18n-helper';
import type { Tournament } from '../../../types/api';

/**
 * Find the tournament ID for a given match
 * @param matchId - The match ID
 * @returns The tournament ID or null
 */
function findTournamentIdForMatch(matchId: string): string | null {
  // Find the select element for this match result
  const selectElement = document.getElementById(`result-${matchId}`);
  if (!selectElement) return null;

  // Traverse up to find the tournament card
  const tournamentCard = selectElement.closest('.tournament-card');
  if (!tournamentCard) return null;

  // Extract tournament ID from any element with the tournament ID in it
  const pairingsDiv = tournamentCard.querySelector('[id^="pairings-tab-"]');
  if (pairingsDiv) {
    return pairingsDiv.id.replace('pairings-tab-', '');
  }

  return null;
}

/**
 * Submit a match result
 * @param matchId - The match ID
 */
export async function submitResult(matchId: string): Promise<void> {
  const selectElement = document.getElementById(`result-${matchId}`) as HTMLSelectElement | null;
  if (!selectElement) return;

  const result = selectElement.value;
  // Store the previous value to revert if user cancels
  const previousValue = selectElement.getAttribute('data-previous-value') || '';

  if (!result) {
    return; // Silently return if no result selected
  }

  // Find the tournament to check format and current round
  const tournamentId = findTournamentIdForMatch(matchId);
  if (tournamentId) {
    try {
      // Fetch tournament data to check format and current round
      const tournamentResponse = await fetch(`${API_BASE_URL}/${tournamentId}`, {
        credentials: 'include',
      });

      if (tournamentResponse.ok) {
        const tournament: Tournament = await tournamentResponse.json();

        // Check if this is a Swiss tournament
        if (tournament.format === 'swiss') {
          // Get the round being edited from the data attribute
          const matchRound = selectElement.getAttribute('data-round');
          const matchRoundNumber = matchRound ? parseInt(matchRound, 10) : null;

          // Find the latest round with matches
          const rounds = tournament.matches?.map(m => m.round) || [];
          const latestRound = rounds.length > 0 ? Math.max(...rounds) : 1;

          // If editing an earlier round and there's a later round paired, ask for confirmation
          if (matchRoundNumber !== null && matchRoundNumber < latestRound) {
            const warningMessage = t('pairings.earlierRoundWarning', 'Note: Round {currentRound} is already paired. Changing results in earlier rounds may affect pairing accuracy.')
              .replace('{currentRound}', String(latestRound));
            const continueText = t('common.continue', 'Continue');
            const cancelText = t('common.cancel', 'Cancel');

            const confirmed = await showWarningConfirmDialog(warningMessage, continueText, cancelText);
            if (!confirmed) {
              // User cancelled - revert the select to previous value
              selectElement.value = previousValue;
              return;
            }
          }
        }
      }
    } catch (error) {
      console.error('Error checking tournament format:', error);
      // Continue with submission even if check fails
    }
  }

  // Determine winner ID based on result
  // Note: We'd need match data to get player IDs, so we'll send null for now
  // The backend can infer from the result format
  const winnerId = null;

  try {
    const response = await fetch(`${API_BASE_URL}/matches/${matchId}/result`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ result, winnerId }),
    });

    if (response.ok) {
      // Visual feedback: briefly highlight the row
      const row = selectElement.closest('tr');
      if (row) {
        const rowElement = row as HTMLElement;
        rowElement.style.backgroundColor = '#d4edda';
        // Fade out the highlight after a short delay
        setTimeout(() => {
          rowElement.style.backgroundColor = '';
        }, 800);
      }

      // Extract tournament ID to update standings and pairings
      const tournamentId = findTournamentIdForMatch(matchId);

      if (tournamentId) {
        // Update standings tab if visible
        const standingsTab = document.getElementById(`standings-${tournamentId}`);
        if (standingsTab && window.loadStandings) {
          window.loadStandings(tournamentId);
        }

        // Refresh pairings display to update the "+round" button visibility
        // IMPORTANT: Await this so keyboard shortcuts can properly select the next match
        // Skip keyboard re-initialization so the keyboard shortcut handler can manage focus navigation
        if (window.refreshTournamentPairings) {
          await window.refreshTournamentPairings(tournamentId, true);
        }
      }
    } else {
      const error = await response.json();
      console.error('Error submitting result:', error.error);
      // Visual feedback for error
      const row = selectElement.closest('tr');
      if (row) {
        (row as HTMLElement).style.backgroundColor = '#f8d7da';
        setTimeout(() => {
          (row as HTMLElement).style.backgroundColor = '';
        }, 1500);
      }
    }
  } catch (error) {
    console.error('Error submitting result:', error);
    // Visual feedback for error
    const row = selectElement.closest('tr');
    if (row) {
      (row as HTMLElement).style.backgroundColor = '#f8d7da';
      setTimeout(() => {
        (row as HTMLElement).style.backgroundColor = '';
      }, 1500);
    }
  }
}

// Export to window for onclick handlers

window.submitResult = submitResult;
